import React, { useEffect, useMemo, useState } from "react";

function api(path, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(`/api${path}`, {
    method: opts.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    credentials: "include",
  }).then(async (res) => {
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`${res.status} ${res.statusText}: ${text}`);
    }
    return res.status === 204 ? null : res.json();
  });
}

// Roles offered in the dropdowns.
//
// `ctc_admin` appears only for a CTC admin, matching the server: it refuses the
// change for anyone else, in both directions. `service` is never offered -- it
// belongs to integration accounts and is not something to hand out by hand --
// but a row that already holds it still displays it truthfully.
const BASE_ROLES = ["user", "admin"];

function viewerRole() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}").role || "";
  } catch {
    return "";
  }
}

function roleOptions(viewer, currentRole) {
  const opts = [...BASE_ROLES];
  if (viewer === "ctc_admin") opts.push("ctc_admin");
  // Always include the value the row actually holds, or the select renders
  // blank and looks like the account has no role at all.
  if (currentRole && !opts.includes(currentRole)) opts.push(currentRole);
  return opts;
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [modules, setModules] = useState([]);
  // userId -> array of granted module ids, loaded lazily when a row is opened
  const [grants, setGrants] = useState({});
  const [openUser, setOpenUser] = useState(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  async function load() {
    try {
      setLoading(true);
      const [u, d, m] = await Promise.all([
        api(`/users`),
        api(`/departments`),
        api(`/modules`),
      ]);
      setUsers(u || []);
      setDepartments(d || []);
      setModules(m || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  // Grants are per-user, so they are fetched on demand rather than N+1 on load.
  async function toggleAccess(userId) {
    if (openUser === userId) { setOpenUser(null); return; }
    setOpenUser(userId);
    if (!grants[userId]) {
      try {
        const res = await api(`/users/${userId}/modules`);
        setGrants((g) => ({ ...g, [userId]: res.module_ids || [] }));
      } catch (e) {
        setErr(e.message);
      }
    }
  }

  async function setGrant(userId, moduleId, checked) {
    const current = grants[userId] || [];
    const next = checked
      ? [...new Set([...current, moduleId])]
      : current.filter((id) => id !== moduleId);
    setGrants((g) => ({ ...g, [userId]: next }));   // optimistic
    try {
      await api(`/users/${userId}/modules`, { method: "PUT", body: { module_ids: next } });
    } catch (e) {
      setErr(e.message);
      setGrants((g) => ({ ...g, [userId]: current }));  // roll back on failure
    }
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(s) ||
      (u.first_name || "").toLowerCase().includes(s) ||
      (u.last_name || "").toLowerCase().includes(s)
    );
  }, [users, q]);

  async function createUser(e) {
    e.preventDefault();
    const f = new FormData(e.target);
    const body = {
      email: (f.get("email") || "").trim().toLowerCase(),
      first_name: f.get("first_name") || "",
      last_name: f.get("last_name") || "",
      role: f.get("role") || "user",
      is_active: true,
      temp_password: f.get("temp_password") || undefined,
      department_id: f.get("department_id") ? Number(f.get("department_id")) : null,
    };
    await api("/users", { method: "POST", body });
    e.target.reset();
    load();
  }

  async function updateUser(id, patch) {
    await api(`/users/${id}`, { method: "PUT", body: patch });
    load();
  }

  async function disableUser(id) {
    if (!window.confirm("Disable this user?")) return;
    await updateUser(id, { is_active: false });
  }

  return (
    <div style={{ padding: "16px" }}>
      <h2>User Management</h2>

      {/* Search */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search users…"
          style={inp}
        />
        <button onClick={() => setQ("")} style={btnGhost}>Clear</button>
      </div>

      {/* Create user */}
      <details style={{ marginBottom: 12 }}>
        <summary style={{ cursor: "pointer" }}>Create User</summary>
        <form onSubmit={createUser} style={{ display: "grid", gap: 8, marginTop: 10 }}>
          <input name="email" placeholder="Email" required style={inp} />
          <input name="first_name" placeholder="First Name" style={inp} />
          <input name="last_name" placeholder="Last Name" style={inp} />
          <select name="role" style={inp}>
            {roleOptions(viewerRole(), null).map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <select name="department_id" style={inp}>
            <option value="">— no department —</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <input name="temp_password" type="password" placeholder="Temp Password (optional)" style={inp} />
          <button style={btnPrimary}>Create</button>
        </form>
      </details>

      <div style={{ border: "1px solid #26324f", borderRadius: 8, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#0c1326" }}>
              {["Email","First","Last","Role","Department","Active","Created","Actions"].map(h => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" style={td}>Loading…</td></tr>}
            {err && <tr><td colSpan="8" style={{...td, color:"red"}}>{err}</td></tr>}
            {!loading && filtered.map((u) => (
              <React.Fragment key={u.id}>
              <tr>
                <td style={td}>{u.email}</td>
                <td style={td}>
                  <input
                    defaultValue={u.first_name || ""}
                    onBlur={(e) => updateUser(u.id, { first_name: e.target.value })}
                    style={cellInput}
                  />
                </td>
                <td style={td}>
                  <input
                    defaultValue={u.last_name || ""}
                    onBlur={(e) => updateUser(u.id, { last_name: e.target.value })}
                    style={cellInput}
                  />
                </td>
                <td style={td}>
                  {/* A customer admin cannot change a CTC admin's role in either
                      direction, so the control is disabled rather than offering
                      a change that only comes back as a 403. */}
                  <select
                    defaultValue={u.role}
                    onChange={(e) => updateUser(u.id, { role: e.target.value })}
                    style={cellInput}
                    disabled={u.role === "ctc_admin" && viewerRole() !== "ctc_admin"}
                    title={
                      u.role === "ctc_admin" && viewerRole() !== "ctc_admin"
                        ? "Only a CTC administrator can change this role"
                        : undefined
                    }
                  >
                    {roleOptions(viewerRole(), u.role).map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </td>
                <td style={td}>
                  <select
                    defaultValue={u.department_id ?? ""}
                    onChange={(e) =>
                      updateUser(u.id, { department_id: e.target.value === "" ? null : Number(e.target.value) })
                    }
                    style={cellInput}
                  >
                    <option value="">— none —</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </td>
                <td style={td}>
                  <input
                    type="checkbox"
                    defaultChecked={u.is_active}
                    onChange={(e) => updateUser(u.id, { is_active: e.target.checked })}
                  />
                </td>
                <td style={td}>
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>
                  <button onClick={() => toggleAccess(u.id)} style={btnGhost}>
                    {openUser === u.id ? "Hide access" : "Module access"}
                  </button>{" "}
                  <button onClick={() => disableUser(u.id)} style={btnDanger}>Disable</button>
                </td>
              </tr>

              {openUser === u.id && (
                <tr>
                  <td colSpan="8" style={{ ...td, background: "#0c1326" }}>
                    <div style={{ marginBottom: 6, opacity: 0.8 }}>
                      Modules granted directly to {u.email}. Access inherited from their
                      department is separate and managed on the Departments page.
                    </div>
                    {modules.length === 0 && <em>No modules configured.</em>}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
                      {modules.map((m) => (
                        <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <input
                            type="checkbox"
                            checked={(grants[u.id] || []).includes(m.id)}
                            onChange={(e) => setGrant(u.id, m.id, e.target.checked)}
                          />
                          {m.name}
                          {m.status !== "active" && (
                            <span style={{ opacity: 0.6 }}> (not licensed)</span>
                          )}
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/*** Styles ***/
const inp = { padding:"8px 10px", borderRadius:8, border:"1px solid #334155", background:"#0f172a", color:"#e6eefc" };
const cellInput = { ...inp, padding:"4px 6px", width:"100%" };
const th = { textAlign:"left", padding:"10px 12px", borderBottom:"1px solid #26324f" };
const td = { padding:"10px 12px", borderBottom:"1px solid #1e293b" };
const btnPrimary = { padding:"8px 12px", background:"#2979ff", color:"#fff", border:"1px solid #2a6bff", borderRadius:8, cursor:"pointer" };
const btnGhost = { padding:"8px 12px", background:"transparent", color:"#e6eefc", borderRadius:8, border:"1px solid #334155", cursor:"pointer" };
const btnDanger = { padding:"6px 10px", background:"#991b1b", color:"#fff", border:"1px solid #7f1d1d", borderRadius:8, cursor:"pointer" };

